<script setup>
import { computed, inject, toValue } from 'vue';
import { useField, FormContextKey } from 'vee-validate';

const props = defineProps({
    id: {
        type: String,
        default: undefined
    },
    // Classes da coluna em volta do campo.
    classeColuna: {
        type: String,
        default: 'col-12'
    },
    // Nome do campo no schema do useForm. Quando informado, o componente grava a lista
    // de valores das opções escolhidas no formulário e lê o erro sozinho.
    name: {
        type: String,
        default: null
    },
    // Opções selecionadas (objetos inteiros), para a view continuar reagindo à escolha.
    modelValue: {
        type: Array,
        default: null
    },
    opcoes: {
        type: Array,
        default: () => []
    },
    titulo: {
        type: String,
        default: ''
    },
    placeholder: {
        type: String,
        default: 'Selecione'
    },
    optionLabel: {
        type: String,
        default: 'nome'
    },
    // Chave da opção que vai para a lista do formulário.
    valorChave: {
        type: String,
        default: 'id'
    },
    // Como as opções escolhidas aparecem no campo: 'chip' ou 'comma'.
    exibicao: {
        type: String,
        default: 'chip'
    },
    // Normalmente dispensável: a obrigatoriedade vem do schema. Serve para forçar o
    // asterisco em regra condicional (`when`), que o schema não expõe estaticamente.
    obrigatorio: {
        type: Boolean,
        default: false
    },
    filtro: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    carregando: {
        type: Boolean,
        default: false
    },
    erro: {
        type: String,
        default: null
    }
});

const emit = defineEmits(['update:modelValue', 'change']);

const campoForm = props.name ? useField(() => props.name) : null;

// Sem v-model, as opções exibidas são deduzidas da lista que está no formulário.
const selecionados = computed({
    get: () => {
        if (props.modelValue !== null) return props.modelValue;
        if (!campoForm) return [];

        const valores = (campoForm.value.value ?? []).map(String);

        // Comparação frouxa: o valor pode voltar da API como string e a opção ter id numérico.
        return props.opcoes.filter((opcao) => valores.includes(String(opcao[props.valorChave])));
    },
    set: (value) => emit('update:modelValue', value)
});

const aoMudar = (evento) => {
    if (campoForm) campoForm.handleChange((evento?.value ?? []).map((opcao) => opcao[props.valorChave]));
    emit('change', evento);
};

const mensagemErro = computed(() => props.erro ?? campoForm?.errorMessage.value ?? null);

const formulario = inject(FormContextKey, null);

// O asterisco de obrigatório sai da própria regra do schema (ex.: regraMultiSelect()
// vs regraMultiSelect({ obrigatorio: false })), para não ser declarado duas vezes.
const obrigatorioDoSchema = computed(() => {
    if (!props.name) return null;

    try {
        const campo = toValue(formulario?.schema)?.describe?.()?.fields?.[props.name];
        return campo ? campo.optional === false : null;
    } catch {
        return null;
    }
});

// O schema só acrescenta o asterisco: a prop continua vencendo, porque um `when`
// aparece como opcional no describe() e ainda assim é obrigatório em tempo de uso.
const obrigatorio = computed(() => props.obrigatorio || obrigatorioDoSchema.value === true);
</script>

<template>
    <div :class="[{ 'field mb-2': props.titulo }, props.classeColuna]">
        <SmallTitle v-if="props.titulo" :titulo="props.titulo" :obrigatorio="obrigatorio" />
        <MultiSelect :inputId="props.id" v-model="selecionados" :options="props.opcoes" :optionLabel="props.optionLabel"
            :display="props.exibicao" :filter="props.filtro" :disabled="props.disabled" :loading="props.carregando"
            :placeholder="props.placeholder" class="w-full text-sm" :class="{ 'p-invalid': mensagemErro }"
            :pt="{ input: { class: 'text-sm' }, item: { class: 'text-sm' } }" @change="aoMudar" />
        <SmallError v-if="mensagemErro" :error="mensagemErro" />
    </div>
</template>
